/* User statistics: quantos usuarios e quantos lancamentos o produto tem, dia a dia.
 *
 * O relogio e `created_at` -- quando a linha foi SALVA -- e nao a data do
 * lancamento. Uma importacao traz anos de extrato numa tarde; pela data do
 * lancamento essa tarde se espalharia por 2023..2027 e o grafico mostraria um
 * crescimento que nao aconteceu. Ver src/generators/activity.py.
 *
 * Quatro graficos em dois pares: o acumulado ao lado do que entrou no dia, um
 * par para usuarios e outro para transacoes, todos sobre o mesmo eixo de dias.
 *
 * Abaixo, as assinaturas por servico: quantos usuarios tem cada uma e quanto ela
 * custa por mes. Essas saem de outro lugar -- o que o `agent_assinaturas` gravou no
 * Supabase, atras do cache de 12 h (app/routers/activity.py) -- e por isso tem
 * query, esqueleto e erro proprios: o Supabase fora do ar nao pode apagar os
 * graficos de cima, que sao do espelho. Os dois graficos saem da MESMA resposta, a
 * analise mais recente de cada usuario, entao um usuario conta uma vez em ambos. */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AgentsCacheMeta } from "../lib/api";
import { brl, brl2, byName, fmtDateTime, intBR } from "../lib/format";
import { ChartFrame, ErrorState, PageHead, Skeleton, Tiles } from "../components/ui";
import { DailyChart, fmtDiaLongo } from "../charts/DailyChart";
import { fmtPercent, ServiceBars } from "../charts/ServiceBars";

interface Activity {
  days: string[];
  new_users: number[];
  users: number[];
  new_transactions: number[];
  transactions: number[];
  timezone: string;
  totals: {
    users: number; transactions: number; days: number; active_days: number;
    first_day: string | null; last_day: string | null;
  };
}

interface Services extends AgentsCacheMeta {
  /** Mais usuarios primeiro; o grafico de gasto reordena. */
  services: {
    name: string; users: number; users_percent: number;
    monthly_brl: number; spend_percent: number | null;
  }[];
  users: number;
  monthly_brl: number;
  items_without_cost: number;
}

/** Nas barras sem centavos ("R$ 1.235"); a tabela mostra o valor exato. */
const reais = (v: number) => `R$ ${brl(v)}`;

const CHARTS: {
  key: keyof Pick<Activity, "users" | "new_users" | "transactions" | "new_transactions">;
  title: string; sub: string; kind: "line" | "bar"; unit: string;
}[] = [
  { key: "users", title: "Usuários únicos ao longo do tempo", kind: "line",
    unit: "usuários", sub: "acumulado" },
  { key: "new_users", title: "Novos usuários por dia", kind: "bar", unit: "usuários",
    sub: "pela primeira transação salva" },
  { key: "transactions", title: "Transações salvas ao longo do tempo", kind: "line",
    unit: "transações", sub: "acumulado" },
  { key: "new_transactions", title: "Novas transações por dia", kind: "bar",
    unit: "transações", sub: "gravadas em cada dia" },
];

export function UserStatistics() {
  const activity = useQuery({
    queryKey: ["activity", "daily"],
    queryFn: () => api<Activity>("/api/activity/daily"),
  });
  const data = activity.data;

  const services = useQuery({
    queryKey: ["activity", "services"],
    queryFn: () => api<Services>("/api/activity/services"),
    // O servidor ja serve o cache vencido quando o Supabase falha; repetir so
    // atrasaria o erro.
    retry: false,
  });
  const sv = services.data;
  const bySpend = sv && [...sv.services].sort((a, b) =>
    b.monthly_brl - a.monthly_brl || byName(a.name, b.name));
  const fonte = sv && <>
    {" · "}{intBR(sv.users)} usuários analisados · dados de {fmtDateTime(sv.fetched_at)}
    {sv.stale && " (Supabase indisponível — última leitura)"}
  </>;

  return (
    <>
      <PageHead eyebrow="atividade" title="User statistics">
        Usuários e transações pelo dia em que foram <strong>salvos</strong> no produto
        (<code>created_at</code>, horário de Brasília) — não pela data do lançamento,
        que numa importação de extrato volta anos. No fim, em quais serviços os usuários
        mais assinam e quanto cada serviço custa por mês.
      </PageHead>

      {activity.isPending && <Skeleton rows={6} height={40} />}
      {activity.error && (
        <ErrorState error={activity.error} onRetry={() => activity.refetch()} />
      )}

      {data && (
        <>
          <Tiles items={[
            { key: "usuarios", label: "Usuários únicos", unit: "n",
              value: data.totals.users,
              detail: "com ao menos uma transação salva" },
            { key: "transacoes", label: "Transações salvas", unit: "n",
              value: data.totals.transactions },
            { key: "dias", label: "Dias no período", unit: "n", value: data.totals.days,
              detail: data.totals.first_day && data.totals.last_day
                ? `${fmtDiaLongo(data.totals.first_day)} a ` +
                  `${fmtDiaLongo(data.totals.last_day)} · ` +
                  `${intBR(data.totals.active_days)} com transações novas`
                : "sem dados" },
          ]} />

          <div className="chartgrid" style={{ marginTop: 18 }}>
            {CHARTS.map((chart) => (
              <ChartFrame key={chart.key} title={chart.title} sub={chart.sub}
                          tableHeader={["dia", chart.unit]}
                          tableRows={data.days.map((day, i) => [day, data[chart.key][i]])}>
                <DailyChart days={data.days} values={data[chart.key]} kind={chart.kind}
                            unit={chart.unit} title={chart.title} />
              </ChartFrame>
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 18, display: "grid", gap: 18 }}>
        {services.isPending && <Skeleton rows={8} height={18} />}
        {services.error && (
          <ErrorState error={services.error} onRetry={() => services.refetch()} />
        )}
        {sv && bySpend && (
          <>
            <ChartFrame
              title="Assinaturas por serviço"
              sub={<>
                quantos usuários têm cada serviço na análise mais recente do{" "}
                <code>agent_assinaturas</code>, do maior para o menor, e que % dos
                usuários analisados isso é · {intBR(sv.services.length)} serviços{fonte}
              </>}
              tableHeader={["serviço", "usuários", "% dos usuários"]}
              tableRows={sv.services.map((s) =>
                [s.name, s.users, fmtPercent(s.users_percent)])}>
              <ServiceBars rows={sv.services.map((s) => (
                { name: s.name, value: s.users, percent: s.users_percent }))} />
            </ChartFrame>

            <ChartFrame
              title="Gasto mensal por serviço"
              sub={<>
                <code>custo_mensal_brl</code> somado por serviço na mesma análise, do maior
                para o menor · {reais(sv.monthly_brl)}/mês no total
                ({reais(sv.monthly_brl * 12)}/ano)
                {sv.items_without_cost > 0 &&
                  ` · ${intBR(sv.items_without_cost)} assinatura(s) sem custo informado`}
                {fonte}
              </>}
              tableHeader={["serviço", "usuários", "R$/mês", "% do gasto"]}
              tableRows={bySpend.map((s) =>
                [s.name, s.users, `R$ ${brl2(s.monthly_brl)}`, fmtPercent(s.spend_percent)])}>
              <ServiceBars format={reais} rows={bySpend.map((s) => (
                { name: s.name, value: s.monthly_brl, percent: s.spend_percent }))} />
            </ChartFrame>
          </>
        )}
      </div>
    </>
  );
}
